import React, { useState, useEffect, useRef, useContext, useCallback } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch, faFileCsv, faCheckCircle, faExclamationTriangle,
  faSyncAlt, faInfoCircle, faUser, faGraduationCap, faTag,
  faSpinner, faDatabase
} from '@fortawesome/free-solid-svg-icons';
import API_CONFIG2 from '../../API_CONFIG/API_CONFIG2';
import Dexie from 'dexie';
import ManageSessions from './ManageSessions';
import { FormationsContext } from '../layout/CunstomFormations'; // ajustez le chemin

// --- Cache Dexie 24h ---
const db = new Dexie('HistoriqueAssignementCacheDB');
db.version(1).stores({
  history: 'key, data, timestamp'
});
const CACHE_DURATION = 24 * 60 * 60 * 1000;

// Fonctions de cache avec possibilité de récupérer même expiré (pour 429)
async function getCachedHistory(key, allowExpired = false) {
  try {
    const entry = await db.history.get(key);
    if (!entry) return null;
    const age = Date.now() - entry.timestamp;
    if (!allowExpired && age > CACHE_DURATION) return null;
    return entry.data;
  } catch { return null; }
}

async function setCachedHistory(key, data) {
  try {
    await db.history.put({ key, data, timestamp: Date.now() });
  } catch { /* ignore */ }
}

async function getStaleCache(key) {
  // Récupère le cache même s'il est expiré (pour fallback 429)
  return getCachedHistory(key, true);
}

const API_URL = `${API_CONFIG2.apiUrl}formation/api_historique_assignement.php`;

// ============================================================
// FONCTION UTILITAIRE POUR LES APPELS FETCH SÉCURISÉS
// ============================================================
const fetchJSON = async (url, options = {}) => {
  const response = await fetch(url, options);
  const text = await response.text();

  if (!response.ok) {
    throw new Error(`HTTP ${response.status} : ${text || 'Erreur inconnue'}`);
  }

  try {
    return JSON.parse(text);
  } catch (parseError) {
    throw new Error(`Réponse JSON invalide : ${text.substring(0, 200) || '(réponse vide)'}`);
  }
};

// ============================================================
// COMPOSANT PRINCIPAL
// ============================================================
const HistoriqueAssignement = ({ userId }) => {
  // Contexte (optionnel)
  const context = useContext(FormationsContext) || {};
  const { refreshKey, setSource } = context;

  // États
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [pagination, setPagination] = useState({ currentPage: 1, totalPages: 1, totalRecords: 0, limit: 10 });
  const [toast, setToast] = useState({ show: false, message: '', variant: 'success' });
  const searchTimeoutRef = useRef(null);
  const abortControllerRef = useRef(null);
  const isMounted = useRef(true);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [dataSource, setDataSource] = useState('cache');

  // Map pour dédoublonner les requêtes en cours
  const pendingFetches = useRef(new Map());

  // --- Gestion des toasts ---
  const showToast = useCallback((message, variant = 'success', duration = 5000) => {
    if (!isMounted.current) return;
    setToast({ show: true, message, variant });
    setTimeout(() => {
      if (isMounted.current) setToast(prev => ({ ...prev, show: false }));
    }, duration);
  }, []);

  // --- FETCH HISTORY avec cache, dédoublonnage, annulation et gestion 429 ---
  const fetchHistory = useCallback(async (page = 1, search = '', forceRefresh = false, retryCount = 0) => {
    // Annuler la requête précédente
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    const cacheKey = `page_${page}_search_${search}`;
    const requestKey = forceRefresh ? `${cacheKey}_force` : cacheKey;

    // Dédoublonnage : si une requête identique est déjà en cours, on l'attend
    if (pendingFetches.current.has(requestKey) && !forceRefresh) {
      try {
        const result = await pendingFetches.current.get(requestKey);
        if (isMounted.current && result) {
          setHistory(result.data);
          setPagination(result.pagination);
          setDataSource('cache (partagé)');
          if (setSource) setSource('cache (partagé)');
        }
        return;
      } catch (err) {
        pendingFetches.current.delete(requestKey);
      }
    }

    // Limiter les tentatives pour éviter boucle infinie
    if (retryCount > 5) {
      if (isMounted.current) {
        setLoading(false);
        setRefreshing(false);
        showToast('Erreur : trop de tentatives. Veuillez réessayer plus tard.', 'danger');
      }
      return;
    }

    // Définir l'état de chargement
    if (forceRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }

    const fetchPromise = (async () => {
      try {
        // 1. Cache si non forcé
        if (!forceRefresh) {
          const cached = await getCachedHistory(cacheKey);
          if (cached && isMounted.current) {
            setHistory(cached.data);
            setPagination(cached.pagination);
            setDataSource('cache');
            if (setSource) setSource('cache');
            return cached;
          }
        }

        // 2. Requête réseau
        const url = `${API_URL}?action=fetch&page=${page}&limit=${pagination.limit}&search=${encodeURIComponent(search)}`;
        const response = await fetch(url, { signal: controller.signal });

        // Gestion 429
        if (response.status === 429) {
          const stale = await getStaleCache(cacheKey);
          if (stale && isMounted.current) {
            setHistory(stale.data);
            setPagination(stale.pagination);
            setDataSource('cache (périmé)');
            if (setSource) setSource('cache (périmé)');
            showToast('429 – Utilisation du cache (peut être obsolète).', 'warning');
            return stale;
          }
          // Sinon réessayer avec backoff
          const waitTime = Math.min(1000 * Math.pow(2, retryCount), 30000);
          await new Promise(resolve => setTimeout(resolve, waitTime));
          return fetchHistory(page, search, forceRefresh, retryCount + 1);
        }

        if (!response.ok) {
          // Fallback cache périmé
          const stale = await getStaleCache(cacheKey);
          if (stale && isMounted.current) {
            setHistory(stale.data);
            setPagination(stale.pagination);
            setDataSource('cache (périmé)');
            if (setSource) setSource('cache (périmé)');
            showToast(`Erreur réseau – Utilisation du cache : ${response.status}`, 'warning');
            return stale;
          }
          throw new Error(`HTTP ${response.status}`);
        }

        // Lecture du JSON
        const text = await response.text();
        let data;
        try {
          data = JSON.parse(text);
        } catch (parseError) {
          throw new Error(`Réponse JSON invalide : ${text.substring(0, 200)}`);
        }

        if (data.status === 'success') {
          const result = { data: data.data, pagination: data.pagination };
          if (isMounted.current) {
            setHistory(data.data);
            setPagination(data.pagination);
            const source = forceRefresh ? 'live (forcé)' : 'live';
            setDataSource(source);
            if (setSource) setSource(source);
            await setCachedHistory(cacheKey, result);
            if (forceRefresh) {
              showToast('Données actualisées avec succès.', 'success');
            }
          }
          return result;
        } else {
          throw new Error(data.message || 'Erreur serveur');
        }
      } catch (err) {
        if (err.name === 'AbortError') return null;
        // Dernier recours : cache périmé
        const stale = await getStaleCache(cacheKey);
        if (stale && isMounted.current) {
          setHistory(stale.data);
          setPagination(stale.pagination);
          setDataSource('cache (périmé)');
          if (setSource) setSource('cache (périmé)');
          showToast(`Erreur – Utilisation du cache : ${err.message}`, 'warning');
          return stale;
        }
        if (isMounted.current) {
          showToast(`Erreur : ${err.message}`, 'danger');
        }
        throw err;
      } finally {
        if (isMounted.current) {
          setLoading(false);
          setRefreshing(false);
        }
      }
    })();

    // Stocker la promesse pour dédoublonner
    pendingFetches.current.set(requestKey, fetchPromise);
    try {
      const result = await fetchPromise;
      return result;
    } finally {
      pendingFetches.current.delete(requestKey);
      if (abortControllerRef.current === controller) {
        abortControllerRef.current = null;
      }
    }
  }, [pagination.limit, setSource, showToast]);

  // --- Gestion de la recherche avec debounce ---
  const handleSearch = (e) => {
    const val = e.target.value;
    setSearchTerm(val);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => fetchHistory(1, val, false), 400);
  };

  // --- Chargement initial ---
  useEffect(() => {
    isMounted.current = true;
    fetchHistory();
    return () => {
      isMounted.current = false;
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
      if (abortControllerRef.current) abortControllerRef.current.abort();
      pendingFetches.current.clear();
    };
  }, []); // eslint-disable-line

  // --- Réaction au refreshKey du contexte ---
  useEffect(() => {
    if (refreshKey && refreshKey > 0) {
      fetchHistory(pagination.currentPage, searchTerm, true);
    }
  }, [refreshKey, pagination.currentPage, searchTerm, fetchHistory]);

  // --- EXPORT CSV (inchangé) ---
  const exportCSV = () => {
    if (!history.length) {
      showToast('Aucune donnée à exporter.', 'warning');
      return;
    }
    const headers = ['ID', 'Assignation ID', 'Action', 'Formation', 'Formateur', 'Anciennes données', 'Nouvelles données', 'Utilisateur', 'Date'];
    const rows = history.map(h => {
      const oldData = h.old_data ? JSON.stringify(h.old_data) : '';
      const newData = h.new_data ? JSON.stringify(h.new_data) : '';
      return [
        h.id,
        h.assignation_id,
        h.action,
        h.formation_titre || '',
        `${h.formateur_prenom || ''} ${h.formateur_nom || ''}`,
        oldData,
        newData,
        h.user_id || '',
        h.date_action
      ];
    });
    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `historique_assignement_${new Date().toISOString().slice(0,10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    showToast('Export CSV réussi', 'success');
  };

  const openDetailModal = (record) => {
    setSelectedRecord(record);
    setShowDetailModal(true);
  };

  const getActionBadge = (action) => {
    const lower = action?.toLowerCase() || '';
    if (lower === 'create') return { label: 'Création', color: '#3b82f6' };
    if (lower === 'delete') return { label: 'Suppression', color: '#ef4444' };
    return { label: action || 'Inconnu', color: '#94a3b8' };
  };

  // ============================================================
  // RENDU (avec améliorations responsives)
  // ============================================================
  return (
    <>
      <style>{`
        /* ============================================================
           DARK BLUE GLASSMORPHISM – HISTORIQUE ASSIGNEMENT (mobile friendly)
           ============================================================ */
        :root {
          --accent-blue: #3b82f6;
          --accent-dark-blue: #1d4ed8;
          --accent-blue-dim: rgba(59, 130, 246, 0.10);
          --accent-blue-glow: rgba(59, 130, 246, 0.25);
          --bg-primary: #0b1428;
          --bg-gradient: radial-gradient(circle at 20% 30%, #132044, #0a0f1f);
          --card-bg: rgba(20, 40, 80, 0.55);
          --card-border: rgba(59, 130, 246, 0.15);
          --text-primary: #e0e7ff;
          --text-secondary: #94a3b8;
          --text-muted: #64748b;
          --radius: 16px;
          --shadow: 0 12px 40px rgba(0, 0, 0, 0.5);
        }

        .historique-page {
          background: var(--bg-gradient);
          min-height: 100vh;
          padding: clamp(0.75rem, 2vw, 2rem);
          color: var(--text-primary);
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
        }

        .glass-card {
          background: var(--card-bg);
          backdrop-filter: blur(16px);
          -webkit-backdrop-filter: blur(16px);
          border: 1px solid var(--card-border);
          border-radius: 24px;
          padding: clamp(1rem, 1.5vw, 1.8rem);
          box-shadow: var(--shadow);
          transition: all 0.2s ease;
          margin-bottom: 24px;
        }
        .glass-card:hover {
          border-color: rgba(59, 130, 246, 0.25);
          box-shadow: 0 12px 40px rgba(59, 130, 246, 0.05);
        }

        .monetra-btn {
          padding: 8px 16px;
          border-radius: 30px;
          border: 1px solid rgba(255,255,255,0.06);
          background: rgba(255,255,255,0.04);
          color: var(--text-primary);
          transition: all 0.25s ease;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-weight: 500;
          cursor: pointer;
          font-size: clamp(0.7rem, 1vw, 0.9rem);
          white-space: nowrap;
          min-height: 44px;
          touch-action: manipulation;
        }
        .monetra-btn:hover:not(:disabled) {
          background: rgba(255,255,255,0.08);
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(0,0,0,0.2);
        }
        .monetra-btn:disabled {
          opacity: 0.5;
          pointer-events: none;
        }
        .monetra-btn.primary {
          background: var(--accent-blue);
          color: #fff;
          border: none;
          box-shadow: 0 6px 20px var(--accent-blue-glow);
        }
        .monetra-btn.primary:hover:not(:disabled) {
          background: var(--accent-dark-blue);
          box-shadow: 0 8px 28px rgba(59, 130, 246, 0.4);
        }
        .monetra-btn.p-2 {
          padding: 6px 10px;
          min-width: 36px;
          justify-content: center;
        }

        .search-wrapper {
          position: relative;
          display: flex;
          align-items: center;
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 30px;
          padding: 4px 4px 4px 16px;
          transition: all 0.3s ease;
          flex: 1;
          min-width: 180px;
        }
        .search-wrapper:focus-within {
          border-color: var(--accent-blue);
          box-shadow: 0 0 20px rgba(59, 130, 246, 0.08);
        }
        .search-wrapper input {
          background: transparent;
          border: none;
          color: var(--text-primary);
          outline: none;
          padding: 10px 0;
          flex: 1;
          font-size: clamp(0.85rem, 1.2vw, 1rem);
        }
        .search-wrapper input::placeholder {
          color: var(--text-muted);
        }

        .badge-source {
          font-size: clamp(0.6rem, 0.9vw, 0.7rem);
          padding: 4px 12px;
          border-radius: 40px;
          background: rgba(59,130,246,0.15);
          color: var(--accent-blue);
          border: 1px solid rgba(59,130,246,0.12);
          white-space: nowrap;
        }
        .badge-source.live {
          background: rgba(46,204,113,0.15);
          color: #2ecc71;
          border-color: rgba(46,204,113,0.12);
        }
        .badge-source.warning {
          background: rgba(245,158,11,0.15);
          color: #f59e0b;
          border-color: rgba(245,158,11,0.12);
        }

        .glass-table {
          width: 100%;
          border-collapse: collapse;
          font-size: clamp(0.7rem, 1vw, 0.85rem);
        }
        .glass-table th {
          padding: 12px 10px;
          text-align: left;
          font-size: clamp(0.6rem, 0.8vw, 0.75rem);
          text-transform: uppercase;
          letter-spacing: 0.5px;
          color: var(--accent-blue);
          border-bottom: 1px solid rgba(255,255,255,0.04);
        }
        .glass-table td {
          padding: 10px 10px;
          border-bottom: 1px solid rgba(255,255,255,0.02);
          vertical-align: middle;
        }
        .glass-table tr {
          transition: 0.2s;
        }
        .glass-table tr:hover {
          background: rgba(255,255,255,0.03);
        }

        .action-badge {
          display: inline-block;
          padding: 3px 10px;
          border-radius: 20px;
          font-size: clamp(0.55rem, 0.8vw, 0.7rem);
          font-weight: 600;
        }

        .monetra-modal .modal-content {
          background: rgba(10, 20, 45, 0.92);
          backdrop-filter: blur(30px);
          -webkit-backdrop-filter: blur(30px);
          border: 1px solid rgba(59, 130, 246, 0.12);
          border-radius: 28px;
          color: var(--text-primary);
          box-shadow: 0 30px 80px rgba(0,0,0,0.8);
        }
        .monetra-modal .modal-header {
          border-bottom: 1px solid rgba(59, 130, 246, 0.06);
        }
        .monetra-modal .modal-footer {
          border-top: 1px solid rgba(59, 130, 246, 0.06);
        }
        .monetra-modal .btn-close {
          filter: invert(1) brightness(200%);
          opacity: 0.5;
        }

        .toast-monetra {
          background: rgba(10, 20, 45, 0.8) !important;
          backdrop-filter: blur(24px) !important;
          -webkit-backdrop-filter: blur(24px) !important;
          border: 1px solid rgba(59, 130, 246, 0.2) !important;
          border-radius: 16px !important;
          color: var(--text-primary) !important;
          box-shadow: 0 20px 50px rgba(0,0,0,0.6) !important;
        }

        .text-secondary { color: var(--text-secondary); }
        .text-muted { color: var(--text-muted); }
        .text-success { color: var(--accent-blue); }
        .text-danger { color: #ef4444; }
        .fw-bold { font-weight: 600; }
        .small { font-size: 0.85rem; }
        .m-0 { margin: 0; }
        .mb-4 { margin-bottom: 1.5rem; }
        .me-1 { margin-right: 0.25rem; }
        .me-2 { margin-right: 0.5rem; }
        .p-2 { padding: 0.5rem; }
        .p-3 { padding: 1rem; }
        .py-5 { padding-top: 3rem; padding-bottom: 3rem; }
        .gap-2 { gap: 0.5rem; }
        .gap-3 { gap: 1rem; }
        .d-flex { display: flex; }
        .flex-wrap { flex-wrap: wrap; }
        .align-items-center { align-items: center; }
        .justify-content-between { justify-content: space-between; }
        .text-end { text-align: right; }
        .text-center { text-align: center; }
        .overflow-hidden { overflow: hidden; }
        .table-responsive { overflow-x: auto; -webkit-overflow-scrolling: touch; }
        .border-top { border-top: 1px solid rgba(255,255,255,0.04); }
        .border-opacity-10 { opacity: 0.1; }
        .row { display: flex; flex-wrap: wrap; }
        .col-md-6 { flex: 0 0 50%; max-width: 50%; padding: 0 12px; }
        .col-12 { flex: 0 0 100%; max-width: 100%; padding: 0 12px; }
        .g-3 { margin: 0 -12px; }

        /* Responsive */
        @media (max-width: 768px) {
          .historique-page { padding: 12px; }
          .glass-card { padding: 1rem; border-radius: 16px; }

          /* Header en colonne */
          .d-flex.justify-content-between.align-items-center {
            flex-direction: column;
            align-items: stretch !important;
            gap: 12px;
          }
          .d-flex.justify-content-between.align-items-center > div:first-child {
            text-align: center;
          }
          .d-flex.justify-content-between.align-items-center .d-flex.gap-2 {
            flex-wrap: wrap;
            justify-content: center;
          }
          .monetra-btn {
            font-size: 0.75rem;
            padding: 6px 12px;
            min-height: 38px;
            flex: 1 0 auto;
            justify-content: center;
          }

          /* Barre de recherche */
          .glass-card.d-flex.justify-content-between.align-items-center {
            flex-direction: column;
            align-items: stretch !important;
          }
          .search-wrapper {
            min-width: unset;
            width: 100%;
            max-width: 100% !important;
          }
          .glass-card .d-flex.align-items-center.gap-2 {
            justify-content: space-between;
            flex-wrap: wrap;
          }

          /* Table */
          .glass-table th, .glass-table td {
            padding: 6px 8px;
            font-size: 0.7rem;
          }
          .glass-table th:last-child, .glass-table td:last-child {
            min-width: 60px;
          }
          .action-badge {
            font-size: 0.6rem;
            padding: 2px 8px;
          }
          /* Pagination */
          .border-top.d-flex {
            flex-direction: column;
            align-items: center !important;
            gap: 8px;
          }
          .monetra-btn.p-2 {
            padding: 4px 12px;
            min-width: 40px;
          }
          /* Modale */
          .monetra-modal .modal-dialog {
            margin: 0.5rem;
          }
          .monetra-modal .modal-content {
            border-radius: 20px;
          }
          .col-md-6 {
            flex: 0 0 100%;
            max-width: 100%;
          }
        }
        @media (max-width: 480px) {
          .monetra-btn {
            font-size: 0.7rem;
            padding: 4px 10px;
            min-height: 34px;
          }
          .glass-table th, .glass-table td {
            font-size: 0.6rem;
            padding: 4px 6px;
          }
        }
      `}</style>

      <div className="historique-page">
        <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
          <div>
            <h1 className="fw-bold mb-1" style={{ fontSize: 'clamp(1.4rem, 4vw, 2.2rem)' }}>
              Historique des assignations
            </h1>
            <p className="text-secondary m-0" style={{ fontSize: 'clamp(0.75rem, 1.2vw, 0.95rem)' }}>
              Consultez toutes les créations et suppressions d'assignations de formation.
            </p>
          </div>
          <div className="d-flex gap-2 flex-wrap align-items-center">
            <button
              className="monetra-btn"
              onClick={() => fetchHistory(pagination.currentPage, searchTerm, false)}
              disabled={loading || refreshing}
            >
              <FontAwesomeIcon icon={faSyncAlt} spin={loading || refreshing} /> Actualiser
            </button>
            <button
              className="monetra-btn"
              onClick={() => fetchHistory(pagination.currentPage, searchTerm, true)}
              disabled={loading || refreshing}
            >
              <FontAwesomeIcon icon={faSyncAlt} spin={loading || refreshing} style={{ color: '#ef4444' }} /> Forcer
            </button>
            <button className="monetra-btn" onClick={exportCSV} disabled={history.length === 0}>
              <FontAwesomeIcon icon={faFileCsv} /> Exporter CSV
            </button>
          </div>
        </div>

        <div className="glass-card mb-4 d-flex justify-content-between align-items-center flex-wrap gap-3">
          <div className="search-wrapper" style={{ minWidth: '260px', flex: 1, maxWidth: '100%' }}>
            <FontAwesomeIcon icon={faSearch} className="text-secondary" />
            <input
              type="text"
              placeholder="Rechercher (action, formation, formateur...)"
              value={searchTerm}
              onChange={handleSearch}
            />
          </div>
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <span className={`badge-source ${dataSource === 'live' ? 'live' : dataSource === 'cache (périmé)' ? 'warning' : ''}`}>
              {dataSource === 'cache' ? '📦 Cache' :
               dataSource === 'cache (périmé)' ? '⚠️ Cache périmé' :
               dataSource === 'cache (partagé)' ? '🔄 Partage' :
               dataSource === 'live' ? '🔄 Live' :
               dataSource === 'live (forcé)' ? '🔄 Forcé' : '📦 Cache'}
            </span>
            <span className="text-secondary small">
              {pagination.totalRecords} enregistrements
            </span>
          </div>
        </div>

        <div className="glass-card p-0 overflow-hidden">
          <div className="table-responsive">
            <table className="glass-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Assignation</th>
                  <th>Action</th>
                  <th>Formation</th>
                  <th>Formateur</th>
                  <th>Anciennes données</th>
                  <th>Nouvelles données</th>
                  <th>Utilisateur</th>
                  <th>Date</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && !refreshing ? (
                  <tr><td colSpan="10" className="text-center py-5"><div className="spinner-border text-primary" role="status"></div></td></tr>
                ) : history.length === 0 ? (
                  <tr><td colSpan="10" className="text-center py-5 text-secondary">Aucun historique trouvé.</td></tr>
                ) : (
                  history.map(item => {
                    const { label, color } = getActionBadge(item.action);
                    return (
                      <tr key={item.id}>
                        <td><span className="fw-bold text-secondary">#{item.id}</span></td>
                        <td><span className="text-secondary">#{item.assignation_id}</span></td>
                        <td>
                          <span className="action-badge" style={{ backgroundColor: color + '22', color: color }}>
                            {label}
                          </span>
                        </td>
                        <td>
                          <span className="text-secondary">
                            <FontAwesomeIcon icon={faGraduationCap} className="me-1" />
                            {item.formation_titre || '-'}
                          </span>
                        </td>
                        <td>
                          <span className="text-secondary">
                            <FontAwesomeIcon icon={faUser} className="me-1" />
                            {item.formateur_prenom && item.formateur_nom ? `${item.formateur_prenom} ${item.formateur_nom}` : '-'}
                          </span>
                        </td>
                        <td className="text-secondary" style={{ maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {item.old_data ? JSON.stringify(item.old_data).substring(0, 40) + '...' : '-'}
                        </td>
                        <td className="text-secondary" style={{ maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {item.new_data ? JSON.stringify(item.new_data).substring(0, 40) + '...' : '-'}
                        </td>
                        <td><span className="text-secondary">{item.user_id || 'Système'}</span></td>
                        <td><span className="text-secondary" style={{ fontSize: '0.85rem' }}>{new Date(item.date_action).toLocaleString()}</span></td>
                        <td className="text-end">
                          <button className="monetra-btn p-2" onClick={() => openDetailModal(item)}>
                            <FontAwesomeIcon icon={faInfoCircle} />
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          <div className="p-3 border-top border-secondary border-opacity-10 d-flex justify-content-between align-items-center flex-wrap">
            <span className="text-secondary small">Page {pagination.currentPage} sur {pagination.totalPages}</span>
            <div className="d-flex gap-2">
              <button
                className="monetra-btn p-2"
                disabled={pagination.currentPage <= 1 || loading || refreshing}
                onClick={() => fetchHistory(pagination.currentPage - 1, searchTerm, false)}
              >
                ‹
              </button>
              <button
                className="monetra-btn p-2"
                disabled={pagination.currentPage >= pagination.totalPages || loading || refreshing}
                onClick={() => fetchHistory(pagination.currentPage + 1, searchTerm, false)}
              >
                ›
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Modale de détails (inchangée) */}
      <Modal show={showDetailModal} onHide={() => setShowDetailModal(false)} centered className="monetra-modal" size="lg">
        <Modal.Header closeButton>
          <Modal.Title className="fw-bold">
            <FontAwesomeIcon icon={faInfoCircle} className="text-success me-2" />
            Détails de l'enregistrement #{selectedRecord?.id}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedRecord && (
            <div className="row g-3">
              <div className="col-md-6">
                <div className="text-secondary small">ID</div>
                <div>#{selectedRecord.id}</div>
              </div>
              <div className="col-md-6">
                <div className="text-secondary small">Assignation ID</div>
                <div>#{selectedRecord.assignation_id}</div>
              </div>
              <div className="col-md-6">
                <div className="text-secondary small">Action</div>
                <div>
                  <span className="action-badge" style={{ backgroundColor: getActionBadge(selectedRecord.action).color + '22', color: getActionBadge(selectedRecord.action).color }}>
                    {getActionBadge(selectedRecord.action).label}
                  </span>
                </div>
              </div>
              <div className="col-md-6">
                <div className="text-secondary small">Formation</div>
                <div>{selectedRecord.formation_titre || '-'}</div>
              </div>
              <div className="col-md-6">
                <div className="text-secondary small">Formateur</div>
                <div>{selectedRecord.formateur_prenom && selectedRecord.formateur_nom ? `${selectedRecord.formateur_prenom} ${selectedRecord.formateur_nom}` : '-'}</div>
              </div>
              <div className="col-md-6">
                <div className="text-secondary small">Utilisateur</div>
                <div>{selectedRecord.user_id || 'Système'}</div>
              </div>
              <div className="col-12">
                <div className="text-secondary small">Anciennes données (JSON)</div>
                <pre className='text-secondary' style={{ background: 'rgba(0,0,0,0.3)', padding: '10px', borderRadius: '8px', overflow: 'auto', maxHeight: '200px' }}>
                  {selectedRecord.old_data ? JSON.stringify(selectedRecord.old_data, null, 2) : 'Aucune'}
                </pre>
              </div>
              <div className="col-12">
                <div className="text-secondary small">Nouvelles données (JSON)</div>
                <pre className='text-secondary' style={{ background: 'rgba(0,0,0,0.3)', padding: '10px', borderRadius: '8px', overflow: 'auto', maxHeight: '200px' }}>
                  {selectedRecord.new_data ? JSON.stringify(selectedRecord.new_data, null, 2) : 'Aucune'}
                </pre>
              </div>
              <div className="col-12">
                <div className="text-secondary small">Date de l'action</div>
                <div>{new Date(selectedRecord.date_action).toLocaleString()}</div>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowDetailModal(false)}>Fermer</Button>
        </Modal.Footer>
      </Modal>

      {/* Toast */}
      {toast.show && (
        <div style={{ position: 'fixed', top: '24px', right: '24px', zIndex: 9999, maxWidth: 'clamp(200px, 30vw, 440px)', width: '100%' }}>
          <div className={`toast-monetra p-3 ${toast.variant}`}>
            <div className="d-flex align-items-center gap-3">
              <FontAwesomeIcon
                icon={toast.variant === 'success' ? faCheckCircle : faExclamationTriangle}
                className={toast.variant === 'success' ? 'text-success' : 'text-danger'}
              />
              <span className="flex-grow-1" style={{ fontSize: 'clamp(0.85rem, 1.2vw, 1rem)' }}>{toast.message}</span>
              <button
                className="btn-close-custom"
                onClick={() => setToast({ ...toast, show: false })}
                style={{ background: 'transparent', border: 'none', color: 'var(--text-primary)', opacity: 0.6, fontSize: '1.2rem', cursor: 'pointer' }}
              >
                ✕
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default HistoriqueAssignement;